import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../../hooks/useAppTranslation'
import { useAuthenticatedUserId } from '../../../../../hooks/useAuthenticatedUserId'
import { useToastNotifications } from '../../../../../hooks/useToastNotification'
import { reverseUrl } from '../../../../../navigation/reverseUrl'
import { theme } from '../../../../../theme/theme'
import { Button, ButtonVariant } from '../../../../common/Button'
import { ToastType } from '../../../../common/Toast/toast-type'
import { ISocietiesModel } from '../../../../../interfaces/models/societies.interfaces'
import { createRequest } from '../../../../../api/requests'
import { i18nSocietyType } from '../../../../../i18n'

interface JoinInactiveSocietyMemberProps {
  society: ISocietiesModel
}

export const JoinInactiveSocietyMember = ({
  society,
}: JoinInactiveSocietyMemberProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const navigate = useNavigate()
  const userId = useAuthenticatedUserId() as string

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('inactiveSociety.flashMessage.joinSocietyFailure', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('inactiveSociety.flashMessage.joinSocietySuccess', {
        context: i18nSocietyType(society?.societyTypeDisplayName),
      })
    )
  }

  const navigateToSociety = (): void => {
    navigate(reverseUrl('society'))
  }

  const { mutate: mutateCreateRequest, isPending } = useMutation({
    mutationFn: createRequest,
    onSuccess: () => {
      showSuccessMessage()
      navigateToSociety()
    },
    onError: () => {
      showErrorMessage()
    },
  })

  const onJoinClick = async (): Promise<void> => {
    mutateCreateRequest({ societyId: society._id, userId })
  }

  return (
    <div className="flex flex-col">
      <p className="mt-4" style={theme.textVariants.lead}>
        {translate('inactiveSociety.member.title', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <p className="max-w-96 mt-4" style={theme.textVariants.base}>
        {translate('inactiveSociety.member.subtitle', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
      </p>
      <Button
        wrapperClassName="w-full"
        className="mt-10 w-full"
        variant={ButtonVariant.PRIMARY}
        label={translate('common.actions.join')}
        onClick={onJoinClick}
        disabled={isPending}
      />
    </div>
  )
}
